// Analytics & Insights (Phase F).
//
// Four tabs against four backend endpoints:
//   - User Activity     -> /admin/analytics/user-activity?range=...
//   - Chatbot Usage     -> /admin/analytics/chatbot-usage?range=...
//   - Spending Patterns -> /admin/analytics/spending?range=...
//   - Platform Health   -> /admin/analytics/platform (no range -- snapshot)
//
// Charts are intentionally hand-rolled with SVG + styled MUI Box so we don't
// pull in a brand-new dependency just for four screens. If a richer chart
// library is added later, the data shapes already match standard
// {x, y} / {label, value} formats so the swap is a render-only change.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Tabs, Tab, ToggleButton, ToggleButtonGroup, Card, CardContent,
    Stack, Grid, CircularProgress, Alert, Chip, Divider, Table, TableHead,
    TableRow, TableCell, TableBody, TableContainer, Paper, Tooltip, IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';

import axiosInstance from '../utils/axiosInstance';
import { formatCurrency } from '../utils/format';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const RANGE_PRESETS = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: '365d', label: 'Last 365 days' },
];

const TABS = [
    { value: 'activity',  label: 'User activity' },
    { value: 'chatbots',  label: 'Chatbot usage' },
    { value: 'spending',  label: 'Spending patterns' },
    { value: 'platform',  label: 'Platform health' },
];

const Analytics = () => {
    const [tab, setTab] = useState('activity');
    const [range, setRange] = useState('30d');

    useEffect(() => { document.title = 'Analytics | Klonit Super Admin'; }, []);
    
    return (
        <Box sx={{ fontFamily }}>
            <Stack direction={{ xs: 'column', sm: 'row' }}
                   justifyContent="space-between"
                   alignItems={{ xs: 'flex-start', sm: 'center' }}
                   spacing={1} sx={{ mb: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>
                        Analytics & Insights
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                        Activity, usage, revenue, and overall platform health.
                    </Typography>
                </Box>
                {tab !== 'platform' && (
                    <ToggleButtonGroup
                        value={range} exclusive size="small" color="primary"
                        onChange={(_, v) => v && setRange(v)}
                    >
                        {RANGE_PRESETS.map((r) => (
                            <ToggleButton key={r.value} value={r.value}
                                          sx={{ fontFamily, textTransform: 'none' }}>
                                {r.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                )}
            </Stack>

            <Tabs
                value={tab} onChange={(_, v) => setTab(v)}
                sx={{ mb: 2, '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 600 } }}
            >
                {TABS.map((t) => <Tab key={t.value} value={t.value} label={t.label} />)}
            </Tabs>

            {tab === 'activity' && <UserActivityTab range={range} />}
            {tab === 'chatbots' && <ChatbotUsageTab range={range} />}
            {tab === 'spending' && <SpendingTab range={range} />}
            {tab === 'platform' && <PlatformTab />}
        </Box>
    );
};

// =============================================================== tab loaders
// Each tab is a thin React component that fetches its own endpoint and
// passes the result to the appropriate chart + table components below.
const useFetch = (url, params, dep) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(url, { params });
            setData(res.data || null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load analytics.');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, dep]);
    useEffect(() => { load(); }, [load]);
    return { data, loading, error, reload: load };
};

const UserActivityTab = ({ range }) => {
    const navigate = useNavigate();
    const { data, loading, error, reload } = useFetch('/admin/analytics/user-activity',
                                                       { range }, range);
    return (
        <TabFrame loading={loading} error={error} onReload={reload}>
            {data && (
                <>
                    <KpiRow items={[
                        { label: 'New signups',   value: data.totals?.new_signups },
                        { label: 'Active users',  value: data.totals?.active_users, accent: '#3b82f6' },
                        { label: 'Total messages', value: data.totals?.total_messages, accent: '#10b981' },
                    ]} />
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} lg={7}>
                            <ChartCard title="Signups by day" subtitle={`${data.start_date} → ${data.end_date}`}>
                                <BarSeries series={data.signups_by_day} valueKey="count"
                                           color="#3b82f6" />
                            </ChartCard>
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <ChartCard title="Top users by messages" subtitle="In selected range">
                                <RankingTable
                                    rows={data.top_active_users || []}
                                    columns={[
                                        { key: 'user_email', label: 'User' },
                                        { key: 'message_count', label: 'Messages', align: 'right' },
                                    ]}
                                    actionLabel="View"
                                    onAction={(r) => navigate(`/users/${r.user_id}`)}
                                />
                            </ChartCard>
                        </Grid>
                    </Grid>
                </>
            )}
        </TabFrame>
    );
};

const ChatbotUsageTab = ({ range }) => {
    const navigate = useNavigate();
    const { data, loading, error, reload } = useFetch('/admin/analytics/chatbot-usage',
                                                       { range }, range);
    return (
        <TabFrame loading={loading} error={error} onReload={reload}>
            {data && (
                <>
                    <KpiRow items={[
                        { label: 'Total messages',     value: data.totals?.total_messages, accent: '#10b981' },
                        { label: 'Active chatbots',    value: data.totals?.distinct_chatbots },
                        { label: 'Avg per chatbot',    value: data.totals?.avg_per_chatbot },
                    ]} />
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} lg={7}>
                            <ChartCard title="Messages by day" subtitle={`${data.start_date} → ${data.end_date}`}>
                                <LineSeries series={data.messages_by_day} valueKey="count"
                                            color="#10b981" />
                            </ChartCard>
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <ChartCard title="Top chatbots by messages" subtitle="Embed traffic only">
                                <RankingTable
                                    rows={data.top_chatbots || []}
                                    columns={[
                                        { key: 'chatbot_name', label: 'Chatbot' },
                                        { key: 'owner_email',  label: 'Owner' },
                                        { key: 'message_count', label: 'Messages', align: 'right' },
                                    ]}
                                    actionLabel="Owner"
                                    onAction={(r) => r.team_owner_id && navigate(`/users/${r.team_owner_id}`)}
                                />
                            </ChartCard>
                        </Grid>
                    </Grid>
                </>
            )}
        </TabFrame>
    );
};

const SpendingTab = ({ range }) => {
    const { data, loading, error, reload } = useFetch('/admin/analytics/spending',
                                                       { range }, range);
    return (
        <TabFrame loading={loading} error={error} onReload={reload}>
            {data && (
                <>
                    <KpiRow items={[
                        { label: 'Revenue', value: formatCurrency(data.totals?.revenue || 0, 'usd'), accent: '#10b981' },
                        { label: 'Successful charges', value: data.totals?.charge_count },
                    ]} />
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} lg={7}>
                            <ChartCard title="Revenue by day" subtitle={`${data.start_date} → ${data.end_date}`}>
                                <BarSeries series={data.revenue_by_day} valueKey="amount"
                                           color="#10b981" formatValue={(v) => formatCurrency(v, 'usd')} />
                            </ChartCard>
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <ChartCard title="Revenue by plan" subtitle="In selected range">
                                <RevenueByPlan rows={data.revenue_by_plan || []} />
                            </ChartCard>
                        </Grid>
                    </Grid>
                </>
            )}
        </TabFrame>
    );
};

const PlatformTab = () => {
    const { data, loading, error, reload } = useFetch('/admin/analytics/platform', null, 'platform');
    return (
        <TabFrame loading={loading} error={error} onReload={reload}>
            {data && (
                <>
                    <KpiRow items={[
                        { label: 'MRR',  value: formatCurrency(data.mrr || 0, 'usd'), accent: '#3b82f6' },
                        { label: 'ARR',  value: formatCurrency(data.arr || 0, 'usd'), accent: '#3b82f6' },
                        { label: 'ARPU', value: formatCurrency(data.arpu || 0, 'usd') },
                        { label: 'Conversion', value: `${(data.conversion_rate_pct || 0).toFixed(1)}%`,
                          accent: '#7c3aed' },
                        { label: 'Paid subscribers',     value: data.paid_subscribers },
                        { label: 'Freemium subscribers', value: data.freemium_subscribers },
                    ]} />
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={6}>
                            <ChartCard title="Subscriptions by status">
                                <CategoryBars
                                    rows={(data.subs_by_status || []).map((s) => ({
                                        label: s.status, value: s.count,
                                    }))}
                                    palette={statusPalette}
                                />
                            </ChartCard>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ChartCard title="Subscribers by plan">
                                <CategoryBars
                                    rows={(data.subs_by_plan || []).map((p) => ({
                                        label: p.display_name || p.plan_name, value: p.count,
                                    }))}
                                    palette={defaultPalette}
                                />
                            </ChartCard>
                        </Grid>
                    </Grid>
                </>
            )}
        </TabFrame>
    );
};

// =============================================================== shared bits
const TabFrame = ({ loading, error, onReload, children }) => {
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (error) {
        return (
            <Stack direction="row" alignItems="center" spacing={1}>
                <Alert severity="error" sx={{ fontFamily, flex: 1 }}>{error}</Alert>
                <IconButton size="small" onClick={onReload}><RefreshIcon fontSize="small" /></IconButton>
            </Stack>
        );
    }
    return <Box>{children}</Box>;
};

const KpiRow = ({ items }) => (
    <Card variant="outlined" sx={{ borderRadius: '14px', mb: 1 }}>
        <CardContent>
            <Grid container spacing={1.5}>
                {items.map((it, i) => (
                    <Grid item xs={6} sm={4} md={2} key={i}>
                        <Box sx={{
                            p: 1.25, borderRadius: '10px',
                            border: '1px solid #e2e8f0',
                            bgcolor: 'rgba(248,250,252,0.6)',
                        }}>
                            <Typography sx={{
                                fontFamily, fontSize: '0.65rem', fontWeight: 700,
                                textTransform: 'uppercase', color: '#94a3b8',
                                letterSpacing: 0.5,
                            }}>
                                {it.label}
                            </Typography>
                            <Typography sx={{
                                fontFamily, fontSize: '1.1rem', fontWeight: 800,
                                color: it.accent || '#0f172a', mt: 0.25,
                            }}>
                                {it.value ?? '--'}
                            </Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </CardContent>
    </Card>
);

const ChartCard = ({ title, subtitle, children }) => (
    <Card variant="outlined" sx={{ borderRadius: '14px', height: '100%',
                                    display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mb: 1.25 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8' }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
            <Divider sx={{ mb: 1.25 }} />
            <Box sx={{ flex: 1, minHeight: 200 }}>{children}</Box>
        </CardContent>
    </Card>
);

// --- BarSeries: hand-rolled CSS bar chart. series = [{date, count|amount}].
const BarSeries = ({ series, valueKey, color, formatValue }) => {
    const values = (series || []).map((d) => Number(d[valueKey]) || 0);
    const max = Math.max(0.01, ...values);
    // For >30 day windows: hide most x-axis labels to avoid overlap. Show
    // every-Nth one based on series length.
    const labelEvery = series.length > 60 ? 14 : series.length > 30 ? 5 : 2;
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.25, height: 180 }}>
                {(series || []).map((d, i) => {
                    const v = Number(d[valueKey]) || 0;
                    const pct = Math.round((v / max) * 100);
                    return (
                        <Tooltip key={i}
                                 title={`${d.date}: ${formatValue ? formatValue(v) : v}`}>
                            <Box sx={{
                                flex: 1, height: `${Math.max(pct, 1)}%`,
                                bgcolor: color, opacity: v > 0 ? 1 : 0.18,
                                borderRadius: '4px 4px 0 0',
                                minHeight: 2,
                            }} />
                        </Tooltip>
                    );
                })}
            </Box>
            <Box sx={{ display: 'flex', mt: 0.75 }}>
                {(series || []).map((d, i) => (
                    <Box key={i} sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography sx={{
                            fontFamily, fontSize: '0.62rem', color: '#94a3b8',
                            visibility: i % labelEvery === 0 ? 'visible' : 'hidden',
                            whiteSpace: 'nowrap',
                        }}>
                            {d.date.slice(5)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

// --- LineSeries: SVG polyline. Same input shape as BarSeries.
const LineSeries = ({ series, valueKey, color }) => {
    const width = 600;
    const height = 180;
    const padding = 8;
    const values = (series || []).map((d) => Number(d[valueKey]) || 0);
    const max = Math.max(0.01, ...values);
    const stepX = (width - padding * 2) / Math.max(1, (series.length - 1));
    const points = (series || []).map((d, i) => {
        const v = Number(d[valueKey]) || 0;
        const x = padding + stepX * i;
        const y = height - padding - ((v / max) * (height - padding * 2));
        return `${x},${y}`;
    }).join(' ');
    const labelEvery = series.length > 60 ? 14 : series.length > 30 ? 5 : 2;
    return (
        <Box>
            <Box sx={{ height: 180, overflow: 'hidden' }}>
                <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none"
                     style={{ width: '100%', height: '100%' }}>
                    {[0.25, 0.5, 0.75].map((p) => (
                        <line key={p}
                              x1={padding} x2={width - padding}
                              y1={padding + (height - padding * 2) * p}
                              y2={padding + (height - padding * 2) * p}
                              stroke="#e2e8f0" strokeWidth="1" />
                    ))}
                    <polyline points={points} fill="none" stroke={color}
                              strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                    {(series || []).map((d, i) => {
                        const v = Number(d[valueKey]) || 0;
                        const x = padding + stepX * i;
                        const y = height - padding - ((v / max) * (height - padding * 2));
                        if (v === 0) return null;
                        return (
                            <Tooltip key={i} title={`${d.date}: ${v}`}>
                                <circle cx={x} cy={y} r="2.5" fill={color} />
                            </Tooltip>
                        );
                    })}
                </svg>
            </Box>
            <Box sx={{ display: 'flex', mt: 0.75 }}>
                {(series || []).map((d, i) => (
                    <Box key={i} sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography sx={{
                            fontFamily, fontSize: '0.62rem', color: '#94a3b8',
                            visibility: i % labelEvery === 0 ? 'visible' : 'hidden',
                            whiteSpace: 'nowrap',
                        }}>
                            {d.date.slice(5)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

// --- CategoryBars: horizontal bar chart for distributions (status/plan).
const defaultPalette = ['#3b82f6', '#10b981', '#7c3aed', '#d97706', '#0ea5e9', '#dc2626', '#64748b'];
const statusPalette = {
    active: '#10b981', trialing: '#3b82f6', freemium: '#94a3b8',
    past_due: '#d97706', canceled: '#64748b', incomplete: '#7c3aed',
    requires_payment_method: '#dc2626',
};

const CategoryBars = ({ rows, palette }) => {
    if (!rows || rows.length === 0) {
        return <EmptyState message="No data." />;
    }
    const max = Math.max(1, ...rows.map((r) => r.value || 0));
    return (
        <Stack spacing={1}>
            {rows.map((r, i) => {
                const color = typeof palette === 'function'
                    ? palette(r.label, i)
                    : (palette && palette[r.label]) || defaultPalette[i % defaultPalette.length];
                const pct = Math.round(((r.value || 0) / max) * 100);
                return (
                    <Box key={r.label}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#475569' }}>
                                {r.label}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color }}>
                                {r.value}
                            </Typography>
                        </Stack>
                        <Box sx={{ height: 8, bgcolor: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                            <Box sx={{
                                width: `${pct}%`, height: '100%', bgcolor: color,
                                transition: 'width 0.25s ease',
                            }} />
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );
};

const RevenueByPlan = ({ rows }) => {
    if (!rows || rows.length === 0) {
        return <EmptyState message="No revenue in this window." />;
    }
    const max = Math.max(0.01, ...rows.map((r) => r.amount || 0));
    return (
        <Stack spacing={1}>
            {rows.map((r, i) => {
                const color = defaultPalette[i % defaultPalette.length];
                const pct = Math.round(((r.amount || 0) / max) * 100);
                return (
                    <Box key={r.plan_name || i}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                    {r.display_name || r.plan_name}
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>
                                    {r.charge_count} charge{r.charge_count !== 1 ? 's' : ''}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 800, color }}>
                                {formatCurrency(r.amount, 'usd')}
                            </Typography>
                        </Stack>
                        <Box sx={{ height: 8, bgcolor: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                            <Box sx={{
                                width: `${pct}%`, height: '100%', bgcolor: color,
                                transition: 'width 0.25s ease',
                            }} />
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );
};

const RankingTable = ({ rows, columns, actionLabel, onAction }) => {
    if (!rows || rows.length === 0) {
        return <EmptyState message="No data." />;
    }
    return (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px' }}>
            <Table size="small">
                <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                    <TableRow>
                        {columns.map((c) => (
                            <TableCell key={c.key} sx={{ fontFamily, fontWeight: 700 }} align={c.align || 'left'}>
                                {c.label}
                            </TableCell>
                        ))}
                        {actionLabel && <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">{actionLabel}</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow key={i} hover>
                            {columns.map((c) => (
                                <TableCell key={c.key} sx={{ fontFamily }} align={c.align || 'left'}>
                                    {r[c.key] ?? '--'}
                                </TableCell>
                            ))}
                            {actionLabel && (
                                <TableCell align="right">
                                    {onAction ? (
                                        <Tooltip title={`Open ${actionLabel.toLowerCase()}`}>
                                            <span>
                                                <IconButton size="small" onClick={() => onAction(r)}
                                                            disabled={!onAction}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    ) : null}
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const EmptyState = ({ message }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
               height: '100%', minHeight: 120, color: '#94a3b8' }}>
        <Typography sx={{ fontFamily, fontSize: '0.85rem' }}>{message}</Typography>
    </Box>
);

export default Analytics;
